import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, LayoutGrid, LogOut, Menu, X } from "lucide-react";

const links = [
  { to: "/portfolio", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/booking", label: "Process & contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user && user.role !== "client";
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0F0D]/90 backdrop-blur-xl" data-testid="navbar">
      <div className="page-container h-[70px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="Craftora home">
          <span className="w-8 h-8 rounded-[9px] bg-[#D8FF5B] text-[#11130F] flex items-center justify-center font-display font-bold text-sm" aria-hidden="true">C</span>
          <span className="font-display font-semibold tracking-[-0.04em] text-xl">Craftora</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary navigation">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/\W+/g, "-")}`}
              className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[#D8FF5B]" : "text-white/65 hover:text-white"}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2.5">
          {user ? (
            <>
              <button onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")} className="pill-btn ghost text-sm py-2 px-4" data-testid="nav-dashboard-btn">
                <LayoutGrid size={14} aria-hidden="true" /> {isAdmin ? "Admin" : "Dashboard"}
              </button>
              <button onClick={async () => { await logout(); navigate("/"); }} className="p-3 text-white/60 hover:text-white transition-colors" aria-label="Sign out" data-testid="nav-logout-btn">
                <LogOut size={17} aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-3 py-2 text-sm text-white/65 hover:text-white transition-colors" data-testid="nav-login-btn">Sign in</Link>
              <Link to="/booking" className="pill-btn accent text-sm py-2 px-4" data-testid="nav-start-btn">Discuss a project <ArrowUpRight size={15} /></Link>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
          className="md:hidden w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5 transition-colors"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div id="mobile-navigation" className="md:hidden min-h-[calc(100vh-70px)] bg-[#0D0F0D] border-t border-white/10 px-5 py-8" data-testid="mobile-navigation">
          <nav className="flex flex-col" aria-label="Mobile navigation">
            <Link to="/" className="font-display text-4xl py-4 border-b border-white/10">Home</Link>
            {links.map((link) => (
              <NavLink key={link.to} to={link.to} className="font-display text-4xl py-4 border-b border-white/10">
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 grid gap-3">
            {user ? (
              <>
                <button onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")} className="pill-btn accent" data-testid="mobile-dashboard-btn">
                  <LayoutGrid size={16} /> {isAdmin ? "Open admin" : "Open dashboard"}
                </button>
                <button onClick={async () => { await logout(); navigate("/"); }} className="pill-btn ghost" data-testid="mobile-logout-btn">
                  <LogOut size={16} /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/booking" className="pill-btn accent" data-testid="mobile-book-btn">Discuss a project <ArrowUpRight size={16} /></Link>
                <Link to="/login" className="pill-btn ghost" data-testid="mobile-login-btn">Client sign in</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
